import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, Eye, FileImage, File, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ResumeCardProps {
  resume: {
    id: string;
    title: string;
    created_at: string;
    is_paid: boolean;
    is_original: boolean;
    status: string;
    preview_content?: string;
    content?: string;
    original_filename?: string;
  };
  onPreview: (resume: any) => void;
  onDownload: (resume: any) => void;
  onDelete: (resume: any) => void;
}

const ResumeCard = ({ resume, onPreview, onDownload, onDelete }: ResumeCardProps) => {
  const getFileTypeIcon = () => {
    if (!resume.original_filename) return <File className="h-8 w-8 text-blue-500" />;
    
    const extension = resume.original_filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FileText className="h-8 w-8 text-red-500" />;
      case 'docx':
      case 'doc':
        return <FileText className="h-8 w-8 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FileImage className="h-8 w-8 text-green-500" />;
      default:
        return <File className="h-8 w-8 text-gray-500" />;
    }
  };
  
  const getStatusColor = () => {
    switch (resume.status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between">
          <div className="flex items-start gap-3">
            {getFileTypeIcon()}
            <div>
              <CardTitle className="text-lg line-clamp-1">{resume.title}</CardTitle>
              <CardDescription>
                Created {new Date(resume.created_at).toLocaleDateString()}
              </CardDescription>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-500" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Resume</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{resume.title}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-500 hover:bg-red-600"
                  onClick={() => onDelete(resume)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-2">
          
          <Badge 
            variant="outline" 
            className={`${getStatusColor()}`}
          >
            {resume.status === 'pending' ? 'Processing...' : 
             resume.status === 'failed' ? 'Failed' : 
             'Ready'}
          </Badge>
          
          <Badge variant={resume.is_paid ? "default" : "outline"} className={
            resume.is_paid ? 'bg-green-500' : 'text-yellow-800 bg-yellow-100'
          }>
            {resume.is_paid ? 'Purchased' : 'Preview Available'}
          </Badge>
        </div>
        
        {resume.content || resume.preview_content ? (
          <div className="text-xs text-gray-500 mt-1">
            {(resume.content || resume.preview_content || '').split(' ').length} words
          </div>
        ) : null}
      </CardContent>
      <CardFooter className="flex justify-between pt-2 border-t">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onPreview(resume)}
                disabled={resume.status === 'pending'}
              >
                <Eye className="mr-1 h-4 w-4" /> Preview
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {resume.status === 'pending' ? 
                'Resume is still processing' : 
                'View resume content'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="default" 
                size="sm"
                onClick={() => onDownload(resume)}
                disabled={resume.status === 'pending'}
              >
                <Download className="h-4 w-4 mr-1" /> 
                {resume.is_paid ? "Download" : "Download"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {resume.is_paid ? 
                'Download optimized resume' : 
                'Download optimized resume'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardFooter>
    </Card>
  );
};

export default ResumeCard;
